import React, { useState } from 'react';
import { X, Send, Check, AlertCircle } from 'lucide-react';
import { t } from '../lib/i18n';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  isDarkMode: boolean;
  /** Oturum açmış kullanıcının adresi — geri bildirime iletişim olarak eklenir. */
  userEmail?: string;
}

/**
 * Geri bildirim kutusu.
 *
 * Gönderene ait adres artık oturumdan geliyor. Eskiden burada geliştiricinin
 * kendi e-postası sabit yazılıydı: kim yazarsa yazsın kayda o adres
 * düşüyordu, yani geri bildirimlerin hiçbiri kime ait olduğu belli değildi.
 */
export const FeedbackModal: React.FC<FeedbackModalProps> = ({
  isOpen,
  onClose,
  isDarkMode,
  userEmail,
}) => {
  const [text, setText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleClose = () => {
    setText('');
    setSent(false);
    setError(null);
    onClose();
  };

  const handleSend = async () => {
    if (!text.trim() || isSending) return;
    setIsSending(true);
    setError(null);
    try {
      await window.zabit.saveFeedback(text.trim(), userEmail ?? '');
      setSent(true);
      setText('');
    } catch (err) {
      // Önceden hata yakalanmıyordu: yazma başarısız olsa bile kullanıcı
      // hiçbir şey görmüyor, metni de kaybediyordu. Artık metin duruyor.
      setError((err as Error).message || t('Geri bildirim kaydedilemedi.', 'Could not save your feedback.'));
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-5 bg-black/80 backdrop-blur-md select-none">
      <div className={`w-full max-w-md rounded-2xl border shadow-2xl overflow-hidden transition-all ${
        isDarkMode ? 'bg-[#19191C] border-[#434349] text-[#F0EEE6]' : 'bg-white border-neutral-200 text-neutral-900'
      }`}>
        <div className={`px-5 py-4 border-b flex items-center justify-between ${
          isDarkMode ? 'border-[#3B3B3F] bg-[#19191C]' : 'border-neutral-200 bg-neutral-50'
        }`}>
          <h3 className="font-bold text-[15px]">{t('Geri Bildirim', 'Feedback')}</h3>
          <button
            onClick={handleClose}
            className="p-1.5 rounded-lg text-neutral-400 hover:text-[#F0EEE6] hover:bg-neutral-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {sent ? (
            <div className="flex items-center gap-2.5 text-emerald-400 text-[14px] font-medium py-2">
              <Check className="w-4.5 h-4.5" />
              {t('Teşekkürler, geri bildiriminiz kaydedildi.', 'Thanks — your feedback has been saved.')}
            </div>
          ) : (
            <>
              <p className="text-[13px] text-neutral-400">
                {t(
                  'Bir öneriniz veya karşılaştığınız bir sorun mu var? Aşağıya yazın.',
                  'Have a suggestion or ran into a problem? Write it below.'
                )}
                {userEmail && (
                  <>
                    {' '}
                    <span className="text-neutral-500">
                      {t('İletişim için', 'We’ll store')} <span className="font-mono">{userEmail}</span>{' '}
                      {t('kaydedilecek.', 'so we can get back to you.')}
                    </span>
                  </>
                )}
              </p>
              <textarea
                autoFocus
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={t('Öneriniz...', 'Your suggestion…')}
                rows={5}
                className={`w-full px-3.5 py-3 rounded-xl border text-[14px] outline-none resize-none transition-colors ${
                  isDarkMode
                    ? 'bg-[#27272B] border-[#424248] text-[#F0EEE6] placeholder:text-neutral-500 focus:border-[#3AC8E0]'
                    : 'bg-neutral-50 border-neutral-300 text-neutral-900 placeholder:text-neutral-400 focus:border-[#3AC8E0]'
                }`}
              />
              {error && (
                <p className="text-[12px] text-rose-400 flex items-center gap-1.5">
                  <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                  {error}
                </p>
              )}
            </>
          )}
        </div>

        <div className={`px-5 py-4 border-t flex items-center justify-end gap-2.5 ${
          isDarkMode ? 'border-[#3B3B3F] bg-[#19191C]' : 'border-neutral-200 bg-neutral-50'
        }`}>
          {sent ? (
            <button
              onClick={handleClose}
              className="px-4 py-2 rounded-xl bg-[#1DA4BA] hover:bg-[#3AC8E0] text-[#F0EEE6] text-[13.5px] font-semibold transition-colors"
            >
              {t('Kapat', 'Close')}
            </button>
          ) : (
            <button
              onClick={handleSend}
              disabled={!text.trim() || isSending}
              className="px-4 py-2 rounded-xl bg-[#1DA4BA] hover:bg-[#3AC8E0] disabled:opacity-50 text-[#F0EEE6] text-[13.5px] font-semibold flex items-center gap-2 transition-colors"
            >
              <Send className="w-3.5 h-3.5" />
              {isSending ? t('Gönderiliyor...', 'Sending…') : t('Gönder', 'Send')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
